import {
  Box,
  DrawerHeader,
  DrawerBody,
  DrawerContent,
  DrawerCloseButton,
  InputGroup,
  InputRightElement,
  Text,
  Input,
  useDisclosure,
  Flex,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { ControllableDrawer, DrawerFixedButton } from 'components/Drawer';
import { FormikSelect } from 'components/Formik';
import AssignEmployeesAndGroupsField from 'containers/JobsTasks/RightDrawer/AssignEmployeesAndGroupsField';
import { countryOptions } from 'global/options';
import { useImportHolidays } from 'hooks/timeOff/useImportHoliday';
import { getFutureYears } from 'utils/helpers';

import HolidayList from './HolidayList';
import { importHolidayValidationSchema, initialImportHolidayData } from './schema';

interface IImportHolidayProps {
  onClose: () => void;
}

const ImportHoliday: React.FC<IImportHolidayProps> = ({ onClose }) => {
  const { t } = useTranslation(['common', 'timeOff']);
  const formikRef = useRef<any>(null);

  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  const { status: saveHolidayStatus, mutateAsync: mutateAsyncSaveHoliday } = useImportHolidays();

  const futureYearList = useMemo(() => {
    return getFutureYears(6).map(year => ({
      label: year.toString(),
      value: year,
    }));
  }, []);

  useEffect(() => {
    if (futureYearList.length > 0 && formikRef.current) {
      formikRef.current.setFieldValue('year', futureYearList[0]);
    }
  }, []);

  const handleFormSubmit = (values: typeof initialImportHolidayData) => {
    const postData = {
      holidays: values.holidays?.length
        ? values.holidays
            .filter(holiday => !holiday?.is_imported)
            .map(holiday => {
              return {
                uuid: holiday.uuid,
                name: holiday.name,
                full_name: holiday.full_name,
                date: holiday.date,
                formatted_date: holiday.formatted_date,
                weekday: holiday.weekday,
                country_code: values.country?.value,
                fixed: holiday.fixed || false,
                global: holiday.global || false,
                repeat_yearly: holiday.repeat_yearly || false,
              };
            })
        : [],
      user_ids: values.users ? values.users.map((user: any) => user.userId) : [],
    };

    mutateAsyncSaveHoliday(postData).then(() => {
      if (onClose) {
        onClose();
      }
    });
  };

  return (
    <Box overflowY="auto">
      <DrawerHeader>Import Holidays</DrawerHeader>
      <DrawerBody paddingBottom="150px">
        <Formik
          initialValues={initialImportHolidayData}
          enableReinitialize
          onSubmit={handleFormSubmit}
          validationSchema={importHolidayValidationSchema}
        >
          {formikBag => {
            formikRef.current = formikBag;
            const { values } = formikBag;
            return (
              <Form noValidate autoComplete="off">
                <Flex gridGap="3">
                  <Box flex="1 1 auto">
                    <FormikSelect
                      name="country"
                      label="Public Holidays in"
                      options={countryOptions}
                      formControlProps={{ isRequired: true }}
                      selectProps={{ isClearable: false, placeholder: 'Select Country' }}
                    />
                  </Box>

                  <Box flex="0 0 110px">
                    <FormikSelect
                      name="year"
                      label="Year"
                      options={futureYearList}
                      selectProps={{ isClearable: false }}
                      formControlProps={{ isRequired: true }}
                    />
                  </Box>
                </Flex>

                {values.country?.value && values.year?.value ? <HolidayList /> : null}

                <Box mt="6">
                  <ControllableDrawer onClose={onDrawerClose} isOpen={isDrawerOpen}>
                    <DrawerContent>
                      <DrawerCloseButton />
                      <AssignEmployeesAndGroupsField />
                    </DrawerContent>
                  </ControllableDrawer>
                  <Text color="gray.500" fontSize="12px" fontWeight="600" mb="1" textTransform="uppercase">
                    Applicable For
                  </Text>

                  <InputGroup size="lg">
                    <Input
                      fontSize="sm"
                      variant="filled"
                      type="text"
                      border="1px"
                      borderColor="inherit"
                      onChange={() => null}
                      value={`${values.users.length || t('common:all')} ${
                        values.users.length === 1 ? t('common:user') : t('common:users')
                      }`}
                    />
                    <InputRightElement
                      width="auto"
                      mr="2"
                      children={
                        <Button size="sm" variant="link" onClick={onDrawerOpen}>
                          {values.users.length ? t('common:edit') : t('common:add')}
                        </Button>
                      }
                    />
                  </InputGroup>
                </Box>

                <DrawerFixedButton>
                  <Button
                    colorScheme="secondary"
                    type="submit"
                    width="100%"
                    size="lg"
                    isLoading={saveHolidayStatus === 'loading'}
                  >
                    Import Holidays
                  </Button>
                </DrawerFixedButton>
              </Form>
            );
          }}
        </Formik>
      </DrawerBody>
    </Box>
  );
};

export default ImportHoliday;
